import { ReactComponent as IconGreenTick } from '@/assets/svgs/green-tick-badge.svg'
import { assetUrl } from '@/utils/asset-url'
import { ReactComponent as IconClose } from '@/assets/svgs/icon-close.svg'
import { ReactComponent as IconReject } from '@/assets/svgs/reject-exclamation.svg'
import Button from '@/components/Button'
import Portal from '@/components/Portal'
import Toast from '@/components/Toast'
import useModal from '@/hooks/use-modal'
import usePage from '@/hooks/use-page'
import usePersonaApprovalRequest from '@/hooks/use-persona-approval-request'
import useToast from '@/hooks/use-toast'
import useUseCases from '@/hooks/use-use-cases'
import { useParams } from '@/lib/router-dom'
import api from "@/services/api/adminApprovalRequest"
import rem from '@/utils/rem'
import titleCase from '@/utils/title-case'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { Fragment, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
// [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR.
// import Breadcrumbs from '@/componentsDS/Breadcrumbs/index'
import Breadcrumb from '@/dsComponents/Breadcrumb';
import { featureConstant } from '@/constants/configuration/feature';
import { useFeature } from "@/hooks/use-feature/index.js";
import { renderUserEmailInline } from "@/utils/formatUserEmail";
import ApprovalRequestModal from '@/dsComponents/ApprovalRequestModal';

const Page = () => {
	/*=================================================
		General
	/================================================*/
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const dateFormat = "DD MMM yyyy, h:mm A"

	const { requestType, requestId } = useParams()
	const [pageTitle, setPageTitle] = useState()
	const [requestAction, setRequestAction] = useState()

	useEffect(() => {
		if (requestType.includes("add")) {
			setPageTitle("Add Persona Request")
			setRequestAction("add")
		}
		else if (requestType.includes("remove")) {
			setPageTitle("Remove Persona Request")
			setRequestAction("remove")
		}
	}, [requestType])

	const statuses = {
		approved: "APPROVED",
		rejected: "REJECTED",
		revoked: "REVOKED",
		pending: "PENDING_APPROVAL",
	}

	const toastType = {
		success: "success",
		error: "error"
	}

	const actionType = {
		approve: 'APPROVE',
		reject: 'REJECT',
	}

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Approval Requests',
				link: `/admin/approval-requests`
			},
			{
				label: 'Persona Change Request',
				link: ``
			},
		]
	 })

	const [rejectReason, setRejectReason] = useState("")
	const [action, setAction] = useState("")

	const [submitLoading, setSubmitLoading] = useState()

	/*=================================================
		API
	/================================================*/

	const {toastList, addToastMessage } = useToast()

	const { currentItem: request, findOne: findRequest } = usePersonaApprovalRequest()
	const { find: findUseCase, currentItem: useCaseDetail, setCurrentItem: setUseCaseItem, error: errorUseCase } = useUseCases()

	useEffect(() => {
		findRequest({requestId})
	}, [])

	useEffect(()=> {
		async function getData(){
			const getUseCaseId = request?.persona[0]?.useCase?.useCaseId
			const useCaseId = {
				useCaseId : getUseCaseId,
				organizationId: request?.organizationId
			};
			const selectedUseCaseItem = await findUseCase(useCaseId)
			if (selectedUseCaseItem && selectedUseCaseItem.length > 0) {
				setUseCaseItem(selectedUseCaseItem[0])
			}
		}
		if(!isEmpty(request)){
			getData()
		}
	}, [request])

	/*=================================================
		Modal
	/================================================*/

	const { visible: visibleConfirmation, show: showConfirmation, hide: hideConfirmation } = useModal()

	/*=================================================
		API Action Functions
	/================================================*/

	async function handleRequest(){

		if (submitLoading) {
			return
		}

		setSubmitLoading(true)

		if (action === actionType.approve){
			const enrolledPersona = request?.persona?.map(persona => {
				return persona.personaId
			})

			const payload = [{
				"adminRequestId": requestId,
				"personaIds": enrolledPersona
			}]

			const [error, result] = await api.approveRequest(payload)

			setSubmitLoading(false)
			if (error) {
				return addToastMessage(error?.message, toastType.error)
			}

			addToastMessage("You have successfully approved the request.", toastType.success)
		}

		if (action === actionType.reject){

			const payload = [{
				"adminRequestId": requestId,
				"comment": rejectReason
			}]

			const [error, result] = await api.rejectRequest(payload)

			setSubmitLoading(false)
			if (error) {
				return addToastMessage(error?.message, toastType.error)
			}

			setRejectReason("")
			findRequest({requestId})
			addToastMessage("You have rejected the request.", toastType.success)
		}

		// Refresh the page
		setTimeout(function () {
			window.location.reload();
		}, 3000);
	}

	return (
		<>
			{/* [DS-MIGRATION] legacy Breadcrumbs - replaced by DS-backed Breadcrumb. Remove in follow-up PR. */}
			{/* <Breadcrumbs breadcrumbItems={breadcrumbsItems} /> */}
			<Breadcrumb items={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>
			</div>

			<hr style={{marginTop: '0'}} />

			<div className="layout-data-elements">
				<div className="layout-data-elements__left">
					<div className="persona-approval__block">
						<div className="persona-approval__header">
							<div className="persona-approval__header__row">
								<div className="persona-approval__header__content">
									<div className="persona-approval__content__avatar" style={{backgroundImage: `url(${request?.user?.organization?.iconUrl})`}}></div>
									<div className="persona-approval__header__content__desc">
										<div className="persona-approval__title">{request?.user?.organization?.name}</div>
										<div className="persona-approval__content__text--md">{moment(request?.dateRequested).format(dateFormat)}</div>
									</div>
								</div>
								<div className="persona-approval__header__buttons">

									{request?.status === statuses.pending && (
									<>
										<Button full maxWidth='90' addClass={"use-case-request-left__right-action-btn"} text="Approve" submit={() => {
											setAction("APPROVE")
											showConfirmation()
										}}/>
										<Button text="Reject" reject submit={() => {
											setAction("REJECT")
											setRejectReason("")
											showConfirmation()
										}}/>
									</>
								)}
								</div>
							</div>
							<div className="persona-approval__content__row">
								<div className="persona-approval__content__text--md" style={{marginTop: rem(16)}}>
									User has submitted a request for the following changes to {request?.organization?.name}'s personas in <strong>{request?.persona[0]?.useCase?.useCaseTitle}</strong>. Please review the details:
								</div>
							</div>
						</div>
						<div className="persona-approval__body">
							<div style={{marginTop: rem(16), marginBottom: rem(16)}}>
								<div className="persona-approval__title">Persona(s) To {titleCase(requestAction)}</div>
							</div>
							<div className="persona-approval__content__text--md--grey">
								{/* {request?.persona?.name} */}
								{request?.persona?.map((persona,index)=>{
									return (
										<Fragment key={index}>
											{persona?.persona?.personaName}<br/>
										</Fragment>
									)
								})}
							</div>
						</div>
					</div>
					<div className="enrolment-request__block">
						<div className="use-case-request-left__bottom-header">
							<div className="enrolment-request__text">
								<div className="enrolment-request__title">Request History</div>
							</div>
						</div>
						<div className="use-case-request-left__block-container">
							{request?.status === statuses.rejected && (
								<div className="use-case-request-left__history-container">
									<IconReject />
									<div className="use-case-request-left__bottom-content">
										<div className="enrolment-request__title--normal-bold">Request Rejected</div>
										<div className="enrolment-request__title--small">by {
											isSoftDeleteUserEnabled ? renderUserEmailInline(request?.modifiedBy) : request?.modifiedBy?.email} on {moment(request?.modified).format("D MMM YYYY, h:mm A")}
										</div>
										<div className="use-case-request-left__history-desc">{`Reason for rejection: ${request?.comment}`}</div>
									</div>
								</div>
							)}
							{request?.status === statuses.approved && (
								<div className="use-case-request-left__history-container">
									<IconGreenTick />
									<div className="use-case-request-left__bottom-content">
										<div className="enrolment-request__title--normal-bold">Request Approved</div>
										<div className="enrolment-request__title--small">by {
											isSoftDeleteUserEnabled ? renderUserEmailInline(request?.modifiedBy) : request?.modifiedBy?.email} on {moment(request?.modified).format("D MMM YYYY, h:mm A")}
										</div>
									</div>
								</div>
							)}
							<div className="use-case-request-left__history-container">
								<IconGreenTick />
								<div className="use-case-request-left__bottom-content">
									<div className="enrolment-request__title--normal-bold">Request Submitted</div>
									<div className="enrolment-request__title--small">by {
										isSoftDeleteUserEnabled ? renderUserEmailInline(request?.user) : request?.user?.email} on {moment(request?.dateRequested).format("D MMM YYYY, h:mm A")}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="layout-data-elements__right">
					<div className="layout-data-elements__block">
						<div className="enrolment-request__title--small">Use Case</div>
						<div className="use-case-request-right__title">{useCaseDetail?.name}</div>
						<div className="use-case-request-right__desc">{useCaseDetail?.description}</div>
					</div>
				</div>
			</div>

			<ApprovalRequestModal
				visible={visibleConfirmation}
				hide={hideConfirmation}
				title={`${titleCase(action)} Request`}
				isApprove={action === actionType.approve}
				isReject={action === actionType.reject}
				onRejectReasonChange={setRejectReason}
				onConfirm={handleRequest}
				submitTestId="btnsubmit"
				reasonTestId="reasontxtarea"
			/>

			{
				submitLoading && (
					ReactDOM.createPortal(
						<div className='modal white'>
							<div className='loading'>
								<img src={assetUrl("/img/loading.gif")} />
							</div>
						</div>
						, document.body
					)
				)
			}

			<Toast toastList={toastList} />
		</>
	)
}

export default Page
